import {
  PauseIcon,
  EyeIcon,
  SunIcon,
  HeartIcon,
  RefreshCwIcon,
  ZapIcon,
  Volume2Icon,
  ArrowLeftRightIcon,
  ShuffleIcon,
  SearchIcon,
  RotateCcwIcon,
  LifeBuoyIcon,
  ClipboardCheckIcon,
  GitBranchIcon,
  MapIcon,
  CalendarDaysIcon,
} from 'lucide-react';

export const CHECKOUT_URL = 'https://calm-en.impultienda.ar/checkout/';

export const IMAGES = {
  mainMockup: 'https://cdn.magicpatterns.com/uploads/oq7rNBzgpDzhhAkZNCRNFX/CALM-EN.png',
  laura: 'https://cdn.magicpatterns.com/uploads/psKPZxXy2M5yrH63YUyJX7/LAURA_MENDES_-_CALM.png',
};

export const DEFAULT_MICROCOPY = 'Instant access · One-time payment of $27 · Lifetime access · 7-Day Guarantee';

export const methodSteps = [
  {
    name: 'Pause',
    icon: PauseIcon,
    circle: 'bg-coral',
    soft: 'bg-coral-soft',
    text: 'text-coral-dark',
    short: 'Slow down before reacting.',
    detail: 'I help you slow down your own reaction before responding.',
  },
  {
    name: 'Observe',
    icon: EyeIcon,
    circle: 'bg-teal',
    soft: 'bg-teal-soft',
    text: 'text-teal-dark',
    short: 'Look at what is actually happening.',
    detail: 'I show you what to look for before deciding what to do.',
  },
  {
    name: 'Reduce',
    icon: SunIcon,
    circle: 'bg-sun',
    soft: 'bg-sun-soft',
    text: 'text-sun-dark',
    short: 'Look for unnecessary stimulation, demands or environmental factors.',
    detail: 'I help you identify unnecessary stimulation, demands or environmental factors you may be able to reduce.',
  },
  {
    name: 'Regulate',
    icon: HeartIcon,
    circle: 'bg-blush',
    soft: 'bg-blush-soft',
    text: 'text-blush-dark',
    short: 'Choose an appropriate supportive strategy.',
    detail: 'I give you practical options to consider based on the situation.',
  },
  {
    name: 'Recover',
    icon: RefreshCwIcon,
    circle: 'bg-lilac',
    soft: 'bg-lilac-soft',
    text: 'text-lilac-dark',
    short: 'Allow time and space to return to routine.',
    detail: 'I show you how to think about the period after the difficult moment and return to the routine gradually.',
  },
];

export const dealingWith = [
  'Intense crying or screaming',
  'Emotional outbursts',
  'Sensory overload',
  'Extreme frustration or irritability',
  'Difficult transitions',
  'Distress when routines change',
  'Overwhelm from noise, lights or busy environments',
  'Bedtime struggles',
  'Moments when your child seems unable to calm down…',
];

export const needToKnow = [
  'What is happening?',
  'What should I do right now?',
  'What should I avoid?',
  'How can I help my child move toward calm?',
];

export const storyTriggers = [
  'Maybe the environment becomes too loud.',
  "Maybe it's time to leave.",
  'Maybe an activity has to end.',
  'Maybe the routine changes unexpectedly.',
  'Maybe your child is simply overwhelmed.',
];

export const storyReactions = [
  'They cover their ears',
  'They push everything away',
  'They run',
  'They shut down',
];

export const storyQuestions = [
  'Should I talk to them?',
  'Should I give them space?',
  'Should I hug them?',
  'Should I turn the lights down?',
  'Should I remove them from the room?',
  'Should I wait?',
  'Am I making it worse?',
];

export const homeProblems = [
  {
    icon: ZapIcon,
    title: 'Your child can go from calm to completely overwhelmed',
    body: "I've designed the system to help you recognize the signs that may appear before a difficult moment escalates.",
    chips: [] as string[],
  },
  {
    icon: Volume2Icon,
    title: 'Certain sounds, lights or environments seem to make everything worse',
    body: "I'll show you how to look at the environment and identify things you may be able to reduce or change.",
    chips: [] as string[],
  },
  {
    icon: ArrowLeftRightIcon,
    title: 'Transitions often become meltdowns',
    body: "I'll give you visual tools and routines to make recurring transitions more predictable.",
    chips: ['Getting dressed', 'Leaving the house', 'Stopping screen time', 'Coming home', 'Going to school', 'Bath time', 'Bedtime'],
  },
  {
    icon: ShuffleIcon,
    title: 'You never know which strategy to try first',
    body: "You've searched Google, saved Instagram posts, watched videos and tried different advice. But when the moment arrives, you still think: “Which one am I supposed to use right now?” That's exactly what I wanted to solve.",
    chips: [] as string[],
  },
  {
    icon: SearchIcon,
    title: 'Afterward, you keep wondering what caused it',
    body: "I'll give you simple tools to record what happened before, during and after difficult moments so you can start noticing patterns specific to your child.",
    chips: [] as string[],
  },
  {
    icon: RotateCcwIcon,
    title: 'Every meltdown feels like starting from zero',
    body: "I don't want that for you. I want you to have a system you can return to again and again.",
    chips: [] as string[],
  },
];

export const beforeItems = [
  'Every meltdown feels like a brand-new emergency',
  'Your mind goes blank: talk to them? Hug them? Wait?',
  'You scroll saved posts hoping to find what to try',
  'Leaving the house, screen time and bedtime turn into battles',
  "Afterward, you replay it wondering what caused it",
  "You're afraid you're making it worse",
];

export const afterItems = [
  'You have a 5-step framework you can remember under pressure',
  'You know what to check first — and what to avoid',
  'SOS cards give you a clear next step in seconds',
  'Visual routines make recurring transitions more predictable',
  'Pattern maps help you notice what happens before a meltdown',
  'You feel more prepared — not perfect, prepared',
];

export const tools = [
  {
    icon: LifeBuoyIcon,
    title: 'SOS Response Cards',
    body: "Quick-reference cards you can keep on your phone or print.",
  },
  {
    icon: ClipboardCheckIcon,
    title: 'Meltdown Preparation Checklists',
    body: 'Simple checklists to help you prepare for situations you already know can be difficult.',
  },
  {
    icon: GitBranchIcon,
    title: 'Decision Flowcharts',
    body: 'Visual “if this → then consider this” frameworks to help you decide what to look at next.',
  },
  {
    icon: MapIcon,
    title: 'Pattern Tracking Maps',
    body: 'Organize what happened before, during and after difficult moments.',
  },
  {
    icon: CalendarDaysIcon,
    title: 'Implementation Planner',
    body: 'A simple way to introduce the system without trying to change everything in your home at once.',
  },
];

export const guaranteeItems = [
  'Read all 6 volumes',
  'Use the SOS cards',
  'Create your first response plan',
  'Try the visual routines',
  'Use the pattern tracker',
  'Start building your Personal Calm Plan™',
];

export const finalNots = [
  "You don't need to memorize hundreds of parenting strategies.",
  "You don't need to become a neurodiversity expert overnight.",
  "You don't need another folder full of articles you'll never find when you need them.",
];

export const buyers = [
  { name: 'Valentina G.', country: 'Mexico' },
  { name: 'Camila R.', country: 'Argentina' },
  { name: 'Sofía M.', country: 'Colombia' },
  { name: 'Isabella P.', country: 'Chile' },
  { name: 'Lucía F.', country: 'Spain' },
  { name: 'Mariana T.', country: 'Peru' },
  { name: 'Daniela S.', country: 'Ecuador' },
  { name: 'Gabriela L.', country: 'Uruguay' },
  { name: 'Andrea V.', country: 'Costa Rica' },
  { name: 'Paula C.', country: 'Dominican Republic' },
  { name: 'Fernanda A.', country: 'Guatemala' },
  { name: 'Natalia O.', country: 'Puerto Rico' },
  { name: 'Carolina B.', country: 'Panama' },
  { name: 'Ximena D.', country: 'Bolivia' },
  { name: 'Renata H.', country: 'Paraguay' },
  { name: 'Alejandra N.', country: 'Venezuela' },
];
